import React, {Fragment, useEffect, useState} from "react";
import {
	Box,
	Button,
	Chip,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	useMediaQuery,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
	CorrectionsStatusEnum,
	TimeClockCorrectionDto,
	TimeClockCrossCheck,
	TimeClockMonthReport,
	TimeClockPersonMonth,
	TimeClockRestControllerApiFactory,
} from "arteco-api-client-ts";
import dayjs from "dayjs";
import {useApiManager} from "../../../../components/utils/api";
import {
	btnSx,
	EmptyState,
	FilterChips,
	ListCard,
	Page,
	PageHeader,
	Panel,
	PersonAvatar,
	SkeletonRows,
	tableContainerSx,
	tableSx,
	Toolbar,
} from "../../../../components/kit";
import {
	duration,
	hhmm,
	hours,
	shortDay,
	STATUS_COLOR,
	STATUS_LABEL
} from "../../../employee/timeclock/timeclock_util";

type Section = "corrections" | "report" | "crosscheck";

const numSx = {fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"} as const;

/** Nota al pie de un bloque: explicación en gris con su icono, en lugar de un aviso de color. */
const Note = ({children}: { children: React.ReactNode }) => (
	<Stack direction="row" spacing={1} sx={{mt: 2, color: "text.secondary"}}>
		<InfoOutlinedIcon sx={{fontSize: 16, mt: "2px"}}/>
		<Typography variant="caption">{children}</Typography>
	</Stack>
);

/**
 * Registro de jornada para RR. HH. (#13709): correcciones que validar, informe mensual por persona con
 * su CSV para la Inspección y el cruce con Redmine que detecta olvidos. No hay alta ni edición de
 * tramos: cada persona ficha desde su portal y lo demás pasa por una corrección con rastro.
 */
export const TimeClockAdminPage = () => {
	const [section, setSection] = useState<Section>("corrections");
	return (
		<Page>
			<PageHeader title="Registro de jornada"
						subtitle="Correcciones que validar, informe mensual para la Inspección y cruce con Redmine."/>
			<Box sx={{mb: 2}}>
				<FilterChips value={section} onChange={setSection} options={[
					{value: "corrections" as Section, label: "Correcciones"},
					{value: "report" as Section, label: "Informe mensual"},
					{value: "crosscheck" as Section, label: "Cruce con Redmine"},
				]}/>
			</Box>
			{section === "corrections" && <CorrectionsTab/>}
			{section === "report" && <ReportTab/>}
			{section === "crosscheck" && <CrossCheckTab/>}
		</Page>
	);
};

const range = (a?: string | null, b?: string | null) => `${hhmm(a)} – ${hhmm(b)}`;

const STATUS_FILTERS = [
	{value: "PENDING", label: "Pendientes"},
	{value: "APPROVED", label: "Aprobadas"},
	{value: "REJECTED", label: "Rechazadas"},
	{value: "TODAS", label: "Todas"},
];

const CorrectionsTab = () => {
	const apiManager = useApiManager();
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));
	const ctrl = apiManager.factory(TimeClockRestControllerApiFactory);
	const [status, setStatus] = useState<string>("PENDING");
	const [rows, setRows] = useState<TimeClockCorrectionDto[]>();
	const [rejecting, setRejecting] = useState<TimeClockCorrectionDto>();
	const [comment, setComment] = useState("");

	const load = () => {
		setRows(undefined);
		apiManager.execute(ctrl.corrections(status === "TODAS" ? undefined : status as CorrectionsStatusEnum))
			.then(setRows).catch(() => setRows([]));
	};
	useEffect(load, [status]);

	const approve = (c: TimeClockCorrectionDto) => {
		apiManager.execute(ctrl.approveCorrection(c.id!, {}), "Corrección aprobada y aplicada").then(load).catch(() => undefined);
	};
	const reject = () => {
		if (!rejecting) {
			return;
		}
		apiManager.execute(ctrl.rejectCorrection(rejecting.id!, {comment}), "Corrección rechazada")
			.then(() => {
				setRejecting(undefined);
				setComment("");
				load();
			})
			.catch(() => undefined);
	};

	const StatusCell = ({c}: { c: TimeClockCorrectionDto }) => (
		<>
			<Chip size="small" label={STATUS_LABEL[c.status || ""]} color={STATUS_COLOR[c.status || ""]}/>
			{c.reviewedByName && (
				<Typography variant="caption" display="block" color="text.secondary" sx={{mt: 0.5}}>
					{c.reviewedByName}{c.reviewedAt ? `, ${dayjs(c.reviewedAt).format("DD/MM HH:mm")}` : ""}
					{c.reviewComment ? `: ${c.reviewComment}` : ""}
				</Typography>
			)}
		</>
	);

	const Actions = ({c}: { c: TimeClockCorrectionDto }) => c.status === "PENDING" ? (
		<Stack direction="row" spacing={1} justifyContent="flex-end">
			<Button size="small" color="error" onClick={() => setRejecting(c)} sx={btnSx}>Rechazar</Button>
			<Button size="small" variant="contained" color="success" onClick={() => approve(c)} sx={btnSx}>Aprobar</Button>
		</Stack>
	) : null;

	return (
		<Panel title="Correcciones" subtitle="Las horas que la plantilla propone para un día sin fichar o con la salida olvidada.">
			<Toolbar left={<FilterChips value={status} onChange={setStatus} options={STATUS_FILTERS}/>}/>

			{!rows ? <SkeletonRows/> : rows.length === 0 ? (
				<EmptyState icon={<CheckCircleRoundedIcon/>} title="No hay correcciones en este estado"/>
			) : compact ? (
				<Stack spacing={1.5}>
					{rows.map(c => (
						<ListCard key={c.id}>
							<Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
								<Stack direction="row" spacing={1.5} alignItems="center" sx={{minWidth: 0}}>
									<PersonAvatar name={c.personName}/>
									<Box sx={{minWidth: 0}}>
										<Typography variant="body2" fontWeight={600} noWrap>{c.personName}</Typography>
										<Typography variant="caption" color="text.secondary">{shortDay(c.workDate)}</Typography>
									</Box>
								</Stack>
								<Chip size="small" label={STATUS_LABEL[c.status || ""]} color={STATUS_COLOR[c.status || ""]}/>
							</Stack>
							<Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 1.5}}>
								<Box>
									<Typography variant="caption" color="text.secondary" display="block">Antes</Typography>
									<Typography variant="body2" sx={numSx}>{c.entryId ? range(c.previousStartAt, c.previousEndAt) : "Sin fichaje"}</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary" display="block">Propuesto</Typography>
									<Typography variant="body2" fontWeight={600} sx={numSx}>
										{range(c.newStartAt ?? c.previousStartAt, c.newEndAt ?? c.previousEndAt)}
									</Typography>
								</Box>
							</Box>
							{c.reason && <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>{c.reason}</Typography>}
							{c.reviewedByName && (
								<Typography variant="caption" display="block" color="text.secondary" sx={{mt: 0.5}}>
									{c.reviewedByName}{c.reviewedAt ? `, ${dayjs(c.reviewedAt).format("DD/MM HH:mm")}` : ""}
									{c.reviewComment ? `: ${c.reviewComment}` : ""}
								</Typography>
							)}
							{c.status === "PENDING" && <Box sx={{mt: 1.5}}><Actions c={c}/></Box>}
						</ListCard>
					))}
				</Stack>
			) : (
				<TableContainer sx={tableContainerSx}>
					<Table size="small" sx={tableSx}>
						<TableHead>
							<TableRow>
								<TableCell>Persona</TableCell>
								<TableCell>Día</TableCell>
								<TableCell>Antes</TableCell>
								<TableCell>Propuesto</TableCell>
								<TableCell>Motivo</TableCell>
								<TableCell>Pedida</TableCell>
								<TableCell>Estado</TableCell>
								<TableCell/>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map(c => (
								<TableRow key={c.id} hover>
									<TableCell>
										<Stack direction="row" spacing={1.5} alignItems="center">
											<PersonAvatar name={c.personName}/>
											<Typography variant="body2" fontWeight={600}>{c.personName}</Typography>
										</Stack>
									</TableCell>
									<TableCell sx={numSx}>{shortDay(c.workDate)}</TableCell>
									<TableCell sx={{...numSx, color: "text.secondary"}}>{c.entryId ? range(c.previousStartAt, c.previousEndAt) : "Sin fichaje"}</TableCell>
									<TableCell sx={{...numSx, fontWeight: 600}}>
										{range(c.newStartAt ?? c.previousStartAt, c.newEndAt ?? c.previousEndAt)}
									</TableCell>
									<TableCell sx={{minWidth: 160}}>{c.reason}</TableCell>
									<TableCell sx={numSx}>
										{c.requestedAt ? dayjs(c.requestedAt).format("DD/MM HH:mm") : ""}
									</TableCell>
									<TableCell><StatusCell c={c}/></TableCell>
									<TableCell align="right"><Actions c={c}/></TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<Note>
				Al aprobar, la hora propuesta se aplica al tramo y la corrección queda como rastro: quién, cuándo,
				valor anterior y nuevo. Nadie valida sus propias correcciones.
			</Note>

			<Dialog open={!!rejecting} onClose={() => setRejecting(undefined)} fullWidth maxWidth="xs" PaperProps={{sx: {borderRadius: 3}}}>
				<DialogTitle sx={{fontWeight: 700}}>Rechazar corrección</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
						{rejecting?.personName}, {shortDay(rejecting?.workDate)}. El tramo no cambia y la persona
						verá el motivo.
					</Typography>
					<TextField fullWidth multiline minRows={2} label="Motivo del rechazo" value={comment}
							   onChange={e => setComment(e.target.value)}/>
				</DialogContent>
				<DialogActions sx={{px: 3, pb: 2.5}}>
					<Button onClick={() => setRejecting(undefined)} sx={btnSx}>Cancelar</Button>
					<Button color="error" variant="contained" disabled={!comment.trim()} onClick={reject} sx={btnSx}>Rechazar</Button>
				</DialogActions>
			</Dialog>
		</Panel>
	);
};

const ReportTab = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(TimeClockRestControllerApiFactory);
	const [month, setMonth] = useState<string>(dayjs().format("YYYY-MM"));
	const [personId, setPersonId] = useState<string>("");
	const [report, setReport] = useState<TimeClockMonthReport>();
	const [people, setPeople] = useState<TimeClockPersonMonth[]>([]);
	const [open, setOpen] = useState<number | undefined>();

	const year = Number(month.substring(0, 4));
	const monthNumber = Number(month.substring(5, 7));

	useEffect(() => {
		if (!year || !monthNumber) {
			return;
		}
		setReport(undefined);
		apiManager.execute(ctrl.monthReport(year, monthNumber, personId ? Number(personId) : undefined))
			.then(r => {
				setReport(r);
				if (!personId) {
					setPeople(r.people || []);
				}
			})
			.catch(() => undefined);
	}, [month, personId]);

	const downloadCsv = () => {
		apiManager.executeRaw(ctrl.monthReportCsv(year, monthNumber, personId ? Number(personId) : undefined,
			{responseType: "text"})).then((resp) => {
			const blob = new Blob([resp.data as unknown as string], {type: "text/csv;charset=utf-8"});
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `registro-jornada-${month}.csv`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		});
	};

	return (
		<Panel title="Informe mensual"
			   subtitle="Por persona: laborables, horas previstas y fichadas, ausencias e incidencias. Pulsa una fila para ver sus días."
			   action={<Button variant="outlined" startIcon={<FileDownloadRoundedIcon/>} onClick={downloadCsv} sx={btnSx}>Descargar CSV</Button>}>
			<Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", sm: "200px minmax(0, 360px)"}, gap: 2, mb: 2}}>
				<TextField fullWidth size="small" type="month" label="Mes" value={month}
						   InputLabelProps={{shrink: true}} onChange={e => setMonth(e.target.value)}/>
				<TextField select fullWidth size="small" label="Persona" value={personId}
						   onChange={e => setPersonId(e.target.value)}>
					<MenuItem value="">Toda la plantilla</MenuItem>
					{people.map(p => <MenuItem key={p.personId} value={String(p.personId)}>{p.personName}</MenuItem>)}
				</TextField>
			</Box>

			{!report ? <SkeletonRows/> : (report.people || []).length === 0 ? (
				<EmptyState title="No hay datos para ese mes"/>
			) : (
				// Tabla ancha: en móvil se desplaza dentro de su caja en lugar de pasar a tarjetas.
				<TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
					<Table size="small" sx={{...tableSx, minWidth: 820}}>
						<TableHead>
							<TableRow>
								<TableCell sx={{width: 48}}/>
								<TableCell>Persona</TableCell>
								<TableCell align="right">Laborables</TableCell>
								<TableCell align="right">Previstas</TableCell>
								<TableCell align="right">Fichadas</TableCell>
								<TableCell align="right">Ausencias</TableCell>
								<TableCell align="right">Festivos</TableCell>
								<TableCell>Incidencias</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{(report.people || []).map(p => (
								<Fragment key={p.personId}>
									<TableRow hover onClick={() => setOpen(open === p.personId ? undefined : p.personId)}
											  sx={{cursor: "pointer"}}>
										<TableCell padding="checkbox">
											<IconButton size="small" aria-label="Ver días">
												{open === p.personId ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
											</IconButton>
										</TableCell>
										<TableCell sx={{whiteSpace: "nowrap"}}>
											<Stack direction="row" spacing={1.5} alignItems="center">
												<PersonAvatar name={p.personName}/>
												<Typography variant="body2" fontWeight={600}>{p.personName}</Typography>
											</Stack>
										</TableCell>
										<TableCell align="right" sx={numSx}>{p.workingDays}</TableCell>
										<TableCell align="right" sx={numSx}>{hours(p.expectedHours)}</TableCell>
										<TableCell align="right" sx={{...numSx, fontWeight: 600}}>{hours(p.workedHours)}</TableCell>
										<TableCell align="right" sx={numSx}>{p.absenceDays}</TableCell>
										<TableCell align="right" sx={numSx}>{p.holidayDays}</TableCell>
										<TableCell>
											<Box display="flex" gap={0.5} flexWrap="wrap">
												{(p.incompleteDays || 0) > 0 && <Chip size="small" color="warning" label={`${p.incompleteDays} incompletos`}/>}
												{(p.missingDays || 0) > 0 && <Chip size="small" color="warning" variant="outlined" label={`${p.missingDays} sin fichar`}/>}
												{(p.pendingCorrections || 0) > 0 && <Chip size="small" color="info" label={`${p.pendingCorrections} por validar`}/>}
												{((p.approvedCorrections || 0) + (p.rejectedCorrections || 0)) > 0 &&
													<Chip size="small" variant="outlined" label={`${(p.approvedCorrections || 0) + (p.rejectedCorrections || 0)} corregidas`}/>}
												{p.warning && <Chip size="small" color="error" label="Sin calendario"/>}
											</Box>
										</TableCell>
									</TableRow>
									<TableRow sx={{"&:hover": {bgcolor: "transparent"}}}>
										<TableCell sx={{py: 0, borderBottom: open === p.personId ? undefined : 0}} colSpan={8}>
											<Collapse in={open === p.personId} unmountOnExit>
												<PersonDays person={p}/>
											</Collapse>
										</TableCell>
									</TableRow>
								</Fragment>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Panel>
	);
};

const PersonDays = ({person}: { person: TimeClockPersonMonth }) => (
	<Box sx={{my: 2}}>
		{person.warning && (
			<Box sx={{mb: 1.5}}><Chip size="small" color="error" label={person.warning}/></Box>
		)}
		<TableContainer sx={tableContainerSx}>
			<Table size="small" sx={tableSx}>
				<TableHead>
					<TableRow>
						<TableCell>Día</TableCell>
						<TableCell>Tramos</TableCell>
						<TableCell align="right">Fichado</TableCell>
						<TableCell align="right">Previsto</TableCell>
						<TableCell>Calendario</TableCell>
						<TableCell>Incidencia</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{(person.days || [])
						.filter(d => (d.entries || []).length > 0 || (d.expectedHours || 0) > 0 || d.absence || d.holiday
							|| (d.corrections || []).length > 0)
						.map(d => (
							<TableRow key={d.date} sx={{opacity: (d.expectedHours || 0) > 0 || (d.entries || []).length > 0 ? 1 : 0.6}}>
								<TableCell sx={numSx}>{shortDay(d.date)}</TableCell>
								<TableCell sx={numSx}>
									{(d.entries || []).map(e => (
										<div key={e.id}>
											{range(e.startAt, e.endAt)}
											{e.origin === "CORRECTION" && " (corregido)"}
											{e.status !== "COMPLETE" && ` · ${STATUS_LABEL[e.status || ""]}`}
										</div>
									))}
								</TableCell>
								<TableCell align="right" sx={numSx}>{(d.entries || []).length > 0 ? duration((d.workedHours || 0) * 60) : ""}</TableCell>
								<TableCell align="right" sx={numSx}>{(d.expectedHours || 0) > 0 ? hours(d.expectedHours) : ""}</TableCell>
								<TableCell>{d.holiday ? `Festivo: ${d.holiday}` : d.absence || ""}</TableCell>
								<TableCell>
									{d.incomplete && <Chip size="small" color="warning" label="Incompleta"/>}
									{d.missing && <Chip size="small" color="warning" variant="outlined" label="Sin fichar"/>}
									{(d.corrections || []).map(c => (
										<Typography key={c.id} variant="caption" display="block">
											Corrección {STATUS_LABEL[c.status || ""]?.toLowerCase()}: {c.reason}
										</Typography>
									))}
								</TableCell>
							</TableRow>
						))}
				</TableBody>
			</Table>
		</TableContainer>
	</Box>
);

const KIND_LABEL: Record<string, string> = {
	NO_TIME_CLOCK: "Horas en Redmine sin fichaje",
	INCOMPLETE_TIME_CLOCK: "Fichaje incompleto",
	MISMATCH: "Fichado e imputado no casan",
};

const KIND_COLOR: Record<string, "warning" | "error" | "info"> = {
	NO_TIME_CLOCK: "error",
	INCOMPLETE_TIME_CLOCK: "warning",
	MISMATCH: "info",
};

const CrossCheckTab = () => {
	const apiManager = useApiManager();
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));
	const ctrl = apiManager.factory(TimeClockRestControllerApiFactory);
	const [from, setFrom] = useState<string>("");
	const [to, setTo] = useState<string>("");
	const [check, setCheck] = useState<TimeClockCrossCheck>();
	const [loading, setLoading] = useState(false);

	const load = (f?: string, t?: string) => {
		setLoading(true);
		apiManager.execute(ctrl.crossCheck(f || undefined, t || undefined))
			.then(c => {
				setCheck(c);
				setFrom(c.from || "");
				setTo(c.to || "");
			})
			.catch(() => undefined)
			.finally(() => setLoading(false));
	};
	useEffect(() => load(), []);

	const rows = check?.rows || [];

	return (
		<Panel title="Cruce con Redmine" subtitle="Días con horas imputadas que no casan con lo fichado. Redmine no es registro: solo detecta olvidos.">
			<Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr 1fr", sm: "180px 180px auto"}, gap: 2, mb: 2, alignItems: "center"}}>
				<TextField fullWidth size="small" type="date" label="Desde" value={from}
						   InputLabelProps={{shrink: true}} onChange={e => setFrom(e.target.value)}/>
				<TextField fullWidth size="small" type="date" label="Hasta" value={to}
						   InputLabelProps={{shrink: true}} onChange={e => setTo(e.target.value)}/>
				<Box sx={{gridColumn: {xs: "1 / -1", sm: "auto"}}}>
					<Button variant="outlined" disabled={loading} onClick={() => load(from, to)} sx={btnSx}>Consultar</Button>
				</Box>
			</Box>

			{loading ? <SkeletonRows/> : check && (
				rows.length === 0 ? (
					<EmptyState icon={<CheckCircleRoundedIcon/>} title="Nada que avisar en el periodo"/>
				) : compact ? (
					<Stack spacing={1.5}>
						{rows.map((r, i) => (
							<ListCard key={i}>
								<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
									<Typography variant="body2" fontWeight={600} noWrap>{r.personName}</Typography>
									<Typography variant="caption" color="text.secondary" sx={numSx}>{shortDay(r.date)}</Typography>
								</Stack>
								<Box sx={{mt: 1}}>
									<Chip size="small" color={KIND_COLOR[r.kind || ""] || "default"} label={KIND_LABEL[r.kind || ""] || r.kind}/>
								</Box>
								<Typography variant="body2" color="text.secondary" sx={{mt: 1, ...numSx}}>
									Redmine {hours(r.imputedHours)} · fichado {hours(r.clockedHours)}
								</Typography>
							</ListCard>
						))}
					</Stack>
				) : (
					<TableContainer sx={tableContainerSx}>
						<Table size="small" sx={tableSx}>
							<TableHead>
								<TableRow>
									<TableCell>Persona</TableCell>
									<TableCell>Día</TableCell>
									<TableCell align="right">Redmine</TableCell>
									<TableCell align="right">Fichado</TableCell>
									<TableCell>Qué pasa</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{rows.map((r, i) => (
									<TableRow key={i} hover>
										<TableCell>
											<Stack direction="row" spacing={1.5} alignItems="center">
												<PersonAvatar name={r.personName}/>
												<Typography variant="body2" fontWeight={600}>{r.personName}</Typography>
											</Stack>
										</TableCell>
										<TableCell sx={numSx}>{shortDay(r.date)}</TableCell>
										<TableCell align="right" sx={numSx}>{hours(r.imputedHours)}</TableCell>
										<TableCell align="right" sx={numSx}>{hours(r.clockedHours)}</TableCell>
										<TableCell>
											<Chip size="small" color={KIND_COLOR[r.kind || ""] || "default"} label={KIND_LABEL[r.kind || ""] || r.kind}/>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)
			)}

			{check && (
				<Note>
					Horas imputadas leídas de: {check.source}. Margen tolerado: 2 h. Cada mañana a las 08:30 se avisa a
					RR. HH. del día laborable anterior, solo si hay algo.
				</Note>
			)}
		</Panel>
	);
};
